import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

// Changing the second factor requires the current password, not just a session.
// A JWT is a session; a stolen one must not be able to enrol an attacker's
// authenticator or turn 2FA off. The password is held in component state only,
// never persisted, and cleared when the flow completes.
export default function AdminTwoFactor() {
  const { user } = useAuth();
  const [step, setStep] = useState('setup');
  const [password, setPassword] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState('');
  const [token, setToken] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const setup2FA = async () => {
    if (!password) {
      toast.error('Enter your current password');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/2fa/setup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (data.qrCode) {
        setQrCode(data.qrCode);
        setSecret(data.secret);
        setStep('verify');
      } else {
        toast.error(data.error || 'Failed to setup 2FA');
      }
    } catch (error) {
      toast.error('Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const verify2FA = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/auth/2fa/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ token })
      });
      const data = await res.json();
      if (data.success) {
        setBackupCodes(data.backupCodes);
        setStep('complete');
        setPassword('');
        toast.success('2FA enabled!');
      } else {
        toast.error(data.error || 'Invalid token');
      }
    } catch (error) {
      toast.error('Failed to verify');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-4">🔐 Admin 2FA Setup</h1>

      {step === 'setup' && (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600 mb-4">
            Two-factor authentication adds an extra layer of security to your admin account.
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 mb-4">
            <li>You'll need Google Authenticator or similar app</li>
            <li>You'll enter a 6-digit code on each login</li>
            <li>Backup codes are issued at the end — write them down</li>
          </ul>

          <label className="block text-sm font-medium text-gray-700 mb-1">
            Current password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Required to change 2FA settings"
            className="w-full border rounded px-3 py-2 mb-4"
            autoComplete="current-password"
          />

          <button
            onClick={setup2FA}
            disabled={loading || !password}
            className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Setting up...' : 'Setup 2FA'}
          </button>
        </div>
      )}

      {step === 'verify' && (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-2">Scan the QR code with Google Authenticator:</p>
          {qrCode && (
            <div className="bg-white p-4 rounded-lg mb-4 text-center">
              <img src={qrCode} alt="QR Code" className="mx-auto max-w-xs" />
            </div>
          )}
          <p className="text-sm text-gray-500 mb-4">
            Secret: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{secret}</code>
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="6-digit code"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="flex-1 border rounded px-3 py-2"
              maxLength={6}
            />
            <button
              onClick={verify2FA}
              disabled={loading || token.length !== 6}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </div>
      )}

      {step === 'complete' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center text-green-600 text-4xl mb-4">✅</div>
          <h2 className="text-xl font-bold text-center mb-4">2FA Enabled!</h2>
          <p className="text-sm text-gray-600 mb-4 text-center">
            Write these codes down and store them securely.
          </p>
          <div className="bg-gray-100 p-4 rounded-lg mb-4 font-mono text-sm grid grid-cols-2 gap-1">
            {backupCodes.map((code, i) => (
              <div key={i} className="text-center">{code}</div>
            ))}
          </div>
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            ⚠️ Backup codes cannot be used to log in yet — that feature is not built.
            If you lose your phone you will be locked out of this admin account until
            a developer disables 2FA on the server. Keep the codes safe regardless;
            they will be honoured once login-time recovery exists.
          </div>
        </div>
      )}
    </div>
  );
}
